import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Heart, Users, FileText, CheckCircle, Clock, Shield, ArrowRight } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

const Services = () => {
  const services = [
    {
      icon: Home,
      title: 'Supported Living',
      description: 'Person-centered support enabling adults with mental health conditions to live independently in the community with dignity, choice, and appropriate assistance.',
      features: [
        'Daily living support and personal care',
        'Medication management and monitoring',
        'Health appointment coordination',
        'Meaningful activity and social inclusion'
      ],
      link: '/supported-living'
    },
    {
      icon: Heart,
      title: 'Mental Health Support',
      description: 'Specialist RMN-led support for individuals with complex mental health needs, promoting recovery, stability, and wellbeing.',
      features: [
        'Risk assessment and management',
        'Crisis prevention and intervention',
        'Liaison with mental health services',
        'Recovery-focused care planning'
      ],
      link: '/services'
    },
    {
      icon: Users,
      title: 'Healthcare Staffing',
      description: 'Professional temporary and permanent staffing solutions for care providers across the UK - RMNs, RGNs, HCAs, and support workers.',
      features: [
        'Qualified mental health nurses (RMNs)',
        'Registered general nurses (RGNs)',
        'Healthcare assistants (HCAs)',
        'Experienced support workers'
      ],
      link: '/staffing'
    },
    {
      icon: FileText,
      title: 'Care Coordination',
      description: 'Comprehensive care planning, multidisciplinary liaison, and holistic support to ensure safe, effective, and responsive care delivery.',
      features: [
        'Personalized care plans',
        'MDT collaboration and liaison',
        'Regular reviews and monitoring',
        'Family and advocate engagement'
      ],
      link: '/services'
    }
  ];

  const supportAreas = [
    {
      icon: CheckCircle,
      title: 'Daily Living Support',
      description: 'Assistance with activities of daily living, personal care, and maintaining healthy routines.'
    },
    {
      icon: Heart,
      title: 'Medication Support',
      description: 'Prompting, administration, and monitoring medication adherence with liaison to prescribers.'
    },
    {
      icon: Users,
      title: 'Health Appointments',
      description: 'Support attending medical and mental health appointments with advocacy and communication.'
    },
    {
      icon: Clock,
      title: 'Wellbeing & Activities',
      description: 'Encouraging meaningful activities, social inclusion, and healthy lifestyle choices.'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7] py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h1 className="heading-hero">Our Services</h1>
            <p className="body-large">
              Comprehensive mental health support and staffing solutions designed to promote recovery, independence, and quality of life.
            </p>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="p-8 hover:shadow-xl transition-all border-0">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#4A90E2] to-[#7B68BE] rounded-xl flex items-center justify-center mb-6">
                    <Icon className="text-white" size={28} />
                  </div>
                  <h3 className="heading-3 mb-4">{service.title}</h3>
                  <p className="body-medium text-gray-600 mb-6">{service.description}</p>
                  <ul className="space-y-3 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle className="text-[#4A90E2] flex-shrink-0 mt-1" size={18} />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to={service.link}>
                    <Button 
                      variant="outline" 
                      className="border-[#4A90E2] text-[#4A90E2] hover:bg-[#4A90E2] hover:text-white"
                    >
                      Learn More
                      <ArrowRight className="ml-2" size={16} />
                    </Button>
                  </Link>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How We Support */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">How We Support</h2>
            <p className="body-large max-w-2xl mx-auto">
              Support is tailored to each individual and delivered in line with personalized care plans and risk assessments.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {supportAreas.map((area, index) => {
              const Icon = area.icon;
              return (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all"
                >
                  <div className="w-12 h-12 bg-[#E8F4FC] rounded-lg flex items-center justify-center mb-4">
                    <Icon className="text-[#4A90E2]" size={24} />
                  </div>
                  <h3 className="font-semibold text-lg mb-3 text-gray-900">{area.title}</h3>
                  <p className="text-sm text-gray-600">{area.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Philosophy */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 mb-8 text-center">Our Philosophy</h2>
            <div className="bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7] rounded-2xl p-8 md:p-12">
              <blockquote className="text-2xl font-semibold text-center mb-6 text-[#2C5F8D]">
                "Health and wellbeing are sustained through balance, structure, and connection."
              </blockquote>
              <p className="body-medium text-center text-gray-700 mb-6">
                Our philosophy recognises that individuals with long-term mental health needs require more than basic care — they need stability, understanding, meaningful relationships, and opportunities for growth.
              </p>
              <div className="grid md:grid-cols-2 gap-6 mt-8">
                <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6">
                  <h4 className="font-semibold text-lg mb-3 text-gray-900">Our Focus</h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-[#4A90E2] flex-shrink-0 mt-0.5" size={16} />
                      <span>Whole-person, recovery-oriented care</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-[#4A90E2] flex-shrink-0 mt-0.5" size={16} />
                      <span>Promoting independence with appropriate support</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-[#4A90E2] flex-shrink-0 mt-0.5" size={16} />
                      <span>Creating safe environments that reduce crisis</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-[#4A90E2] flex-shrink-0 mt-0.5" size={16} />
                      <span>Empowering confidence, routine, and purpose</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6">
                  <h4 className="font-semibold text-lg mb-3 text-gray-900">Our Approach</h4>
                  <p className="text-sm text-gray-700">
                    Our services are shaped by real-world clinical experience, reflective practice, and continuous learning, ensuring care is ethical, proportionate, and responsive to individual needs.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-gradient-to-r from-[#4A90E2] to-[#7B68BE] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="heading-2 text-white">Discuss Your Care Needs</h2>
            <p className="body-large text-white/90">
              Contact us to learn more about our services, make a referral, or discuss partnership opportunities.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#4A90E2] hover:bg-gray-100">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
